import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { SelectedLocationService, SiteLocation } from '@app/data/services/locationServices';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { EChartsOption } from 'echarts';
import {
  getTaongaChartConfig,
  TaongaReportingData,
  TaongaData,
  AbundanceList,
} from './rongo-taonga-abundance-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown } from '@app/widgets/helpers/organisation-breakdown';

@Component({
  selector: 'app-rongo-taonga-abundance-chart',
  templateUrl: './rongo-taonga-abundance-chart.component.html',
  styleUrls: ['./rongo-taonga-abundance-chart.component.scss'],
})
export class RongoTaongaAbundanceChartComponent implements OnInit {
  chartTitle: string = 'Rongo Taonga';
  chartSubtitle: string = '';
  chartOptions: any = null;
  id: string = 'rongo-taonga-abundance';
  startDate: any = '';
  endDate: any = '';
  location: SiteLocation | null = null;
  reportingData: TaongaReportingData | {} = {};
  taongaData: TaongaData | {} = {};
  abundance: AbundanceList[] = [];
  availableOptions: string[] = [];
  selectedOptions: string[] = [];
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<TaongaReportingData>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService,
    private selectedLocationService: SelectedLocationService
  ) {}

  ngOnInit() {
    this.selectedLocationService.subscribe(this.handleLocationUpdate);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService
      .getTaongaAbundanceData(this.startDate, this.endDate, this.location ? this.location.id : null, 'organisation')
      .subscribe(
        (data: RongoReportingData<TaongaReportingData>) => {
          const { config, data: taongaAbundanceData, breakdown } = data;
          this.chartTitle = config.title;
          this.chartSubtitle = config.subTitle || '';
          this.reportingData = taongaAbundanceData;
          this.taongaData = taongaAbundanceData.taongaMedians;
          this.abundance = taongaAbundanceData.abundance;
          this.breakdown = breakdown?.groups || null;
          this.noData = Object.keys(taongaAbundanceData.taongaMedians).length === 0;
          this.availableOptions = Object.keys(this.taongaData);
          this.selectedOptions = this.availableOptions.slice(0, 5); // Default to first 10 options
          this.setChartData();
        },
        (error: any) => {
          console.error('Error fetching data:', error);
        }
      );
  };

  setChartData = () => {
    const filterData = (taonga: TaongaData) =>
      this.selectedOptions.reduce((acc, key) => {
        if (key in taonga) {
          return { ...acc, [key]: taonga[key] };
        }
        return acc;
      }, {} as TaongaData);

    this.chartOptions = withOrganisationBreakdown(
      getTaongaChartConfig(this.abundance, filterData(this.taongaData as TaongaData)),
      this.breakdown,
      {
        buildGroupOption: (d: TaongaReportingData) => getTaongaChartConfig(d.abundance, filterData(d.taongaMedians)),
        flatten: (d: TaongaReportingData) =>
          Object.entries(d.taongaMedians).map(([name, details]) => ({ name, value: details.medianOrdinal })),
      }
    );
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleLocationUpdate = (location: SiteLocation | null) => {
    if (!location && !this.location) return;
    if (location?.id != this.location?.id) {
      this.location = location;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  handleTaongaListChange(event: MatSelectChange): void {
    this.setChartData();
  }
}
